import { deliveryMenuErrorMessage } from '@/utils/deliveryMenuSync'

/**
 * 배달 메뉴 동기화 화면의 **대상 매장 축**.
 *
 * 배달 어드민 API 는 전부 `storeCd` 를 명시로 받는다 — 세션에 "현재 작업 매장" 을 담지 않는 것이
 * 규약이다(§3.2 · §15 비목표). 탭을 여러 개 열어 서로 다른 매장을 보면서 같은 화면이라고 믿는
 * 사고를 막기 위한 결정이라, 여기서도 값을 모듈 전역에 캐시하지 않고 컴포넌트 상태로만 둔다.
 *
 * 스코프 자체는 서버가 강제한다(admin/mng=전체, custMng=자기 사업장, storeMng=자기 시블링 그룹).
 *
 * ## 이 화면들이 부를 수 있는 API 는 사장님 계정이 가진 것뿐이다
 *
 * **403 은 이 앱에서 치명적이다** — `plugins/http` 의 인터셉터가 **어떤 403 이든** 세션을 지우고
 * `/login?redirect_reason=403` 으로 보낸다. 컴포넌트의 `try/catch` 는 그보다 늦게 돌아 아무것도
 * 막지 못한다. 그래서 "실패해도 그만인 부가 조회" 라는 것이 여기엔 없다.
 *
 * 초판은 형제 매장 이름을 얻으려고 `GET /api/shop-in-shop/group/{storeCd}` 를 불렀고, 그때 그 경로는
 * `sys_auth_x_resources` 상 **admin·custMng·mng 에만** 열려 있어 정작 주 사용자인 `storeMng`(사장님)가
 * 들어오면 403 → 강제 로그아웃이었다. 그래서 이 화면들은 매장명을 **서버가 이미 주는 응답에서**
 * 가져온다 — ② 매트릭스의 `stores[]`, ① 상세의 `siblingDeliveryOverlap[]` 이 그것이다.
 * 같은 사실을 다른 경로로 다시 얻으려 하지 않는다.
 *
 * ★ **그 권한 서술은 더 이상 사실이 아니다** (2026-09-05 실측). §16.152 가 `resources_cd 645`
 * (`/api/shop-in-shop/group/**` GET)를 **네 롤 전부**(admin·custMng·mng·storeMng)에 열었고, dev 에서
 * 사장님 계정으로 불러 200 과 형제 목록을 받았다. 품절 화면(`PosDeliverySoldoutView`)의 매장 전환은
 * 그 경로를 직접 쓴다. **이 문단을 「그러니 못 부른다」로 읽지 마라** — 위 「응답이 준 이름을 쓴다」는
 * 여전히 좋은 규칙이지만(같은 사실을 두 번 얻지 않는다), 그 근거가 403 이라는 것은 옛말이다.
 */
export default {
  data() {
    return {
      scopeStoreCd: null,
      scopeStoreNm: '',
      /** storeCd → storeNm. **서버 응답이 이름을 실어 줄 때만** 채워진다 */
      scopeStoreNames: {},
    }
  },

  methods: {
    /** 세션 매장을 대상 축의 기본값으로 해석한다. `/store/getStore` 는 storeMng 도 가진 경로다 */
    async loadScopeStore() {
      try {
        const res = await this.$axios.get('/store/getStore', { showLoading: false })
        const store = res.data || {}
        this.scopeStoreCd = store.storeCd != null ? Number(store.storeCd) : null
        this.scopeStoreNm = store.storeNm || ''
        return this.scopeStoreCd
      } catch (e) {
        this.$message({ type: 'error', message: deliveryMenuErrorMessage(e, '매장 정보를 불러오지 못했습니다.') })
        return null
      }
    },

    /**
     * 서버 응답이 실어 준 `[{storeCd, storeNm}]` 를 이름 사전에 넣는다.
     * 이름의 출처는 언제나 그 코드를 함께 내려 준 응답이다 — 따로 조회하지 않는다.
     */
    rememberStoreNames(refs) {
      if (!Array.isArray(refs)) return
      const next = { ...this.scopeStoreNames }
      for (const ref of refs) {
        if (ref && ref.storeCd != null && ref.storeNm) next[Number(ref.storeCd)] = ref.storeNm
      }
      this.scopeStoreNames = next
    },

    /** storeCd → 사람이 읽는 이름. 이름을 받은 적 없는 매장은 코드로 보여준다 */
    scopeStoreName(storeCd) {
      if (storeCd == null) return '-'
      const hit = this.scopeStoreNames[Number(storeCd)]
      if (hit) return hit
      if (Number(storeCd) === Number(this.scopeStoreCd) && this.scopeStoreNm) return this.scopeStoreNm
      return `#${storeCd}`
    },
  },
}
